import { getCdnUrl } from '@desygner/ui-core-cdn-mapping';

export const isUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Returns a version-specific URL for an image.
 *
 * This function modifies the input URL to point to a specific version of the image.
 * The version is determined by the `size` parameter. If the image is an SVG, it returns
 * the original URL since SVGs are scalable by nature.
 *
 * @param {string} url - The original URL of the image.
 * @param {ImageVersion} size - The desired image version.
 * @param {boolean} [isQa=false] - A flag indicating whether the environment is QA.
 * @returns {string} The version-specific URL of the image.
 */
export const getVersionUrl = (url: string, size: ImageVersion, isQa?: boolean): string => {
  if (!isUrl(url)) return url;

  const isImageSvg = url.endsWith('.svg');

  if (isImageSvg) {
    return getCdnUrl(url, isQa);
  }

  if (isAmazonImage(url)) {
    let replacement = '';
    const convertFormats = ['/thumb/', '/mobile/', '/tab/', '/web/', '/largeweb/', '/original/'];

    convertFormats.forEach((format) => {
      const position = url.indexOf(format);

      if (position !== -1 && !replacement) {
        replacement = format;
      }
    });

    if (replacement) {
      url = url.replace(replacement, '/' + size + '/');
    } else if (url.indexOf('/img/backgrounds/') > -1) {
      convertFormats.forEach((format) => {
        format = '-' + format.replace(/\//g, '') + '.';
        const position = url.indexOf(format);

        if (position !== -1 && !replacement) {
          replacement = format;
        }
      });

      url = url.replace(replacement, '-' + size + '.');
    }
  }

  const replacedUrl = url.replace('/original/', '/' + size + '/');

  return getCdnUrl(replacedUrl, isQa);
};

/**
 * Checks if a URL points to an AmazonAws image.
 *
 * This function checks if the input URL points to an image hosted on Amazon S3 or on certain static domains.
 * It returns `true` if the URL matches any of the specified patterns, and `false` otherwise.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} `true` if the URL points to an Amazon image, `false` otherwise.
 */
export const isAmazonImage = (url: string): boolean => {
  if (!url) return false;

  return (
    url.match(/^((http|https):\/\/s3.amazonaws.com)/) !== null ||
    url.indexOf('static.inkive.me') > -1 ||
    url.indexOf('static.inkive.com') > -1 ||
    url.indexOf('static.webrand.com') > -1 ||
    url.indexOf('static.desygner.com') > -1 ||
    url.indexOf('static.photodexia.com') > -1 ||
    url.indexOf('static.makewebrand.xyz') > -1 ||
    url.indexOf('static.makedesygner.xyz') > -1 ||
    url.indexOf('static.buildwebrand.xyz') > -1 ||
    url.indexOf('static.builddesygner.xyz') > -1 ||
    url.indexOf('static.qawebrand.xyz') > -1 ||
    url.indexOf('static.qadesygner.xyz') > -1
  );
};

export type ImageVersion = 'thumb' | 'mobile' | 'tab' | 'web' | 'largeweb' | 'original';

export function removeTrailingSlash(text: string): string {
  return text.endsWith('/') ? text.slice(0, -1) : text;
}

export function removeLeadingSlash(text: string): string {
  return text.startsWith('/') ? text.slice(1) : text;
}

export function removeLeadingAndTrailingSlashes(text: string): string {
  return removeTrailingSlash(removeLeadingSlash(text));
}

/**
 * Returns the thumbnail of a video.
 *
 * This function generates a thumbnail from a video and returns it as a data URL.
 *
 * @param {string} source - The source of the video.
 * @returns {Promise<string>} The data URL of the video thumbnail.
 */

export async function getVideoThumbnail(source: string): Promise<string> {
  const videoExtensions = ['mp4', 'mov', 'gif'];
  const extension = source.split('.').pop()?.toLowerCase();

  if (!extension || !videoExtensions.includes(extension)) {
    return source; // Return the original source if it's not a video
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');

    video.src = source;
    video.crossOrigin = 'anonymous'; // Prevent CORS issues
    video.muted = true; // Required for autoplay in some browsers
    video.playsInline = true;

    video.addEventListener('loadeddata', () => {
      video.currentTime = Math.min(1, video.duration / 2); // Capture frame at 1s or middle of the video
    });

    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);

            resolve(blobUrl); // Resolve with the Blob URL
            video.remove();
          } else {
            reject(new Error('Failed to convert canvas to Blob'));
            video.remove();
          }
        }, 'image/png'); // Specify the image format
      } else {
        reject(new Error('Failed to get canvas context'));
        video.remove();
      }
    });

    video.addEventListener('error', () => {
      reject(new Error('Failed to load video'));
      video.remove();
    });
  });
}
